import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const TABS = ['Trivia', 'Videos', 'Surveys', 'Blog Topics', 'Blog Submissions'];

export const AdminEarnFeatures = () => {
  const [tab, setTab] = useState('Trivia');
  return (
    <div className="page" style={{ maxWidth: '100%' }}>
      <h1 className="page-title">Earn Features</h1>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button
            key={t}
            className="btn-sm"
            onClick={() => setTab(t)}
            style={{
              background: tab === t ? 'var(--primary)' : 'transparent',
              color: tab === t ? '#fff' : 'var(--text-muted)',
              borderColor: tab === t ? 'var(--primary)' : 'var(--border)',
            }}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 'Trivia' && <TriviaAdmin />}
      {tab === 'Videos' && <VideosAdmin />}
      {tab === 'Surveys' && <SurveysAdmin />}
      {tab === 'Blog Topics' && <BlogTopicsAdmin />}
      {tab === 'Blog Submissions' && <BlogSubmissionsAdmin />}
    </div>
  );
};

// ── TRIVIA ─────────────────────────────────────────────────────
const TriviaAdmin = () => {
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState({ question: '', options: ['', '', '', ''], correct_index: 0 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/admin/trivia-questions').then(r => setQuestions(r.data.questions));
  useEffect(() => { load(); }, []);

  const setOption = (i, val) => setForm(f => ({ ...f, options: f.options.map((o, idx) => idx === i ? val : o) }));

  const resetForm = () => { setForm({ question: '', options: ['', '', '', ''], correct_index: 0 }); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const options = form.options.map(o => o.trim()).filter(Boolean);
    if (!form.question.trim() || options.length < 2) {
      toast.error('A question and at least 2 options are required');
      return;
    }
    setLoading(true);
    try {
      const payload = { question: form.question.trim(), options, correct_index: form.correct_index };
      if (editing) {
        await api.patch(`/admin/trivia-questions/${editing}`, payload);
        toast.success('Question updated');
      } else {
        await api.post('/admin/trivia-questions', payload);
        toast.success('Question added');
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save question');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (q) => {
    setEditing(q.id);
    const options = [...q.options];
    while (options.length < 4) options.push('');
    setForm({ question: q.question, options, correct_index: q.correct_index });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggle = async (id, is_active) => { await api.patch(`/admin/trivia-questions/${id}`, { is_active }); load(); };
  const remove = async (id) => { if (!window.confirm('Delete this question?')) return; await api.delete(`/admin/trivia-questions/${id}`); toast.success('Deleted'); load(); };

  return (
    <div>
      <div className="form-card" style={{ maxWidth: 600 }}>
        <h2 style={{ marginBottom: 20 }}>{editing ? 'Edit Question' : 'Add Trivia Question'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Question *</label>
            <input value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} placeholder="e.g. What is the capital of Kenya?" required />
          </div>
          {form.options.map((opt, i) => (
            <div className="form-group" key={i}>
              <label>
                <input
                  type="radio"
                  name="correct"
                  checked={form.correct_index === i}
                  onChange={() => setForm(f => ({ ...f, correct_index: i }))}
                  style={{ width: 'auto', marginRight: 8 }}
                />
                Option {i + 1} {form.correct_index === i ? '(correct)' : ''}
              </label>
              <input value={opt} onChange={e => setOption(i, e.target.value)} placeholder={`Option ${i + 1}`} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="btn-primary" disabled={loading} style={{ flex: 1 }}>
              {loading ? 'Saving...' : editing ? 'Update Question' : 'Add Question'}
            </button>
            {editing && <button type="button" className="btn-primary" style={{ flex: 1, background: '#334155' }} onClick={resetForm}>Cancel</button>}
          </div>
        </form>
      </div>

      <h2 style={{ marginBottom: 16, fontSize: '1.1rem' }}>All Questions ({questions.length})</h2>
      <div className="table-wrapper">
        <table className="data-table">
          <thead><tr><th>Question</th><th>Correct Answer</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {questions.map(q => (
              <tr key={q.id}>
                <td style={{ maxWidth: 260 }}>{q.question}</td>
                <td>{q.options[q.correct_index]}</td>
                <td><span className={`badge ${q.is_active ? 'active' : 'inactive'}`}>{q.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="action-btns">
                  <button className="btn-sm" onClick={() => startEdit(q)}>Edit</button>
                  <button className="btn-sm" style={{ color: q.is_active ? '#ef4444' : '#22c55e', borderColor: q.is_active ? '#ef4444' : '#22c55e' }} onClick={() => toggle(q.id, !q.is_active)}>
                    {q.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button className="btn-sm red" onClick={() => remove(q.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {questions.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#64748b', padding: 32 }}>No questions yet. You need at least 5 active ones for Trivia to work.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── VIDEOS ─────────────────────────────────────────────────────
const VideosAdmin = () => {
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState({ title: '', video_url: '', duration_seconds: 30 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/admin/earn-videos').then(r => setVideos(r.data.videos));
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: '', video_url: '', duration_seconds: 30 }); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.video_url.trim()) { toast.error('Title and video URL are required'); return; }
    setLoading(true);
    try {
      if (editing) {
        await api.patch(`/admin/earn-videos/${editing}`, form);
        toast.success('Video updated');
      } else {
        await api.post('/admin/earn-videos', form);
        toast.success('Video added');
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save video');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (v) => { setEditing(v.id); setForm({ title: v.title, video_url: v.video_url, duration_seconds: v.duration_seconds }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const toggle = async (id, is_active) => { await api.patch(`/admin/earn-videos/${id}`, { is_active }); load(); };
  const remove = async (id) => { if (!window.confirm('Delete this video?')) return; await api.delete(`/admin/earn-videos/${id}`); toast.success('Deleted'); load(); };

  return (
    <div>
      <div className="form-card" style={{ maxWidth: 600 }}>
        <h2 style={{ marginBottom: 20 }}>{editing ? 'Edit Video' : 'Add Ad Video'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Safaricom Data Bundles Ad" required />
          </div>
          <div className="form-group">
            <label>Video URL (direct .mp4 link) *</label>
            <input value={form.video_url} onChange={e => setForm({ ...form, video_url: e.target.value })} placeholder="https://..." required />
          </div>
          <div className="form-group">
            <label>Duration (seconds) *</label>
            <input type="number" min="5" value={form.duration_seconds} onChange={e => setForm({ ...form, duration_seconds: Number(e.target.value) })} required />
            <small>Users must watch this long before the reward is claimable.</small>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="btn-primary" disabled={loading} style={{ flex: 1 }}>
              {loading ? 'Saving...' : editing ? 'Update Video' : 'Add Video'}
            </button>
            {editing && <button type="button" className="btn-primary" style={{ flex: 1, background: '#334155' }} onClick={resetForm}>Cancel</button>}
          </div>
        </form>
      </div>

      <h2 style={{ marginBottom: 16, fontSize: '1.1rem' }}>All Videos ({videos.length})</h2>
      <div className="table-wrapper">
        <table className="data-table">
          <thead><tr><th>Title</th><th>Duration</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {videos.map(v => (
              <tr key={v.id}>
                <td><strong>{v.title}</strong></td>
                <td>{v.duration_seconds}s</td>
                <td><span className={`badge ${v.is_active ? 'active' : 'inactive'}`}>{v.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="action-btns">
                  <button className="btn-sm" onClick={() => startEdit(v)}>Edit</button>
                  <button className="btn-sm" style={{ color: v.is_active ? '#ef4444' : '#22c55e', borderColor: v.is_active ? '#ef4444' : '#22c55e' }} onClick={() => toggle(v.id, !v.is_active)}>
                    {v.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button className="btn-sm red" onClick={() => remove(v.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {videos.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#64748b', padding: 32 }}>No videos yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── SURVEYS ────────────────────────────────────────────────────
const SurveysAdmin = () => {
  const [surveys, setSurveys] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', reward_amount: 50, questionsText: '' });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/admin/surveys').then(r => setSurveys(r.data.surveys));
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: '', description: '', reward_amount: 50, questionsText: '' }); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const questions = form.questionsText.split('\n').map(q => q.trim()).filter(Boolean).map(q => ({ question: q }));
    if (!form.title.trim() || questions.length === 0) { toast.error('Title and at least 1 question are required'); return; }
    setLoading(true);
    try {
      const payload = { title: form.title.trim(), description: form.description, reward_amount: form.reward_amount, questions };
      if (editing) {
        await api.patch(`/admin/surveys/${editing}`, payload);
        toast.success('Survey updated');
      } else {
        await api.post('/admin/surveys', payload);
        toast.success('Survey created');
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save survey');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({ title: s.title, description: s.description || '', reward_amount: s.reward_amount, questionsText: s.questions.map(q => q.question || q).join('\n') });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const toggle = async (id, is_active) => { await api.patch(`/admin/surveys/${id}`, { is_active }); load(); };
  const remove = async (id) => { if (!window.confirm('Delete this survey?')) return; await api.delete(`/admin/surveys/${id}`); toast.success('Deleted'); load(); };

  return (
    <div>
      <div className="form-card" style={{ maxWidth: 600 }}>
        <h2 style={{ marginBottom: 20 }}>{editing ? 'Edit Survey' : 'Create Survey'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Mobile Money Habits" required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Short description shown to users" />
          </div>
          <div className="form-group">
            <label>Reward (KES) *</label>
            <input type="number" min="1" value={form.reward_amount} onChange={e => setForm({ ...form, reward_amount: Number(e.target.value) })} required />
          </div>
          <div className="form-group">
            <label>Questions (one per line) *</label>
            <textarea rows={5} value={form.questionsText} onChange={e => setForm({ ...form, questionsText: e.target.value })} placeholder={'How often do you use mobile money?\nWhat would you improve?'} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="btn-primary" disabled={loading} style={{ flex: 1 }}>
              {loading ? 'Saving...' : editing ? 'Update Survey' : 'Create Survey'}
            </button>
            {editing && <button type="button" className="btn-primary" style={{ flex: 1, background: '#334155' }} onClick={resetForm}>Cancel</button>}
          </div>
        </form>
      </div>

      <h2 style={{ marginBottom: 16, fontSize: '1.1rem' }}>All Surveys ({surveys.length})</h2>
      <div className="table-wrapper">
        <table className="data-table">
          <thead><tr><th>Title</th><th>Questions</th><th>Reward</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {surveys.map(s => (
              <tr key={s.id}>
                <td><strong>{s.title}</strong></td>
                <td>{s.questions.length}</td>
                <td>KES {s.reward_amount}</td>
                <td><span className={`badge ${s.is_active ? 'active' : 'inactive'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="action-btns">
                  <button className="btn-sm" onClick={() => startEdit(s)}>Edit</button>
                  <button className="btn-sm" style={{ color: s.is_active ? '#ef4444' : '#22c55e', borderColor: s.is_active ? '#ef4444' : '#22c55e' }} onClick={() => toggle(s.id, !s.is_active)}>
                    {s.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button className="btn-sm red" onClick={() => remove(s.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {surveys.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 32 }}>No surveys yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── BLOG TOPICS ────────────────────────────────────────────────
const BlogTopicsAdmin = () => {
  const [topics, setTopics] = useState([]);
  const [form, setForm] = useState({ title: '', brief: '', min_words: 150, reward_amount: 30 });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/admin/blog-topics').then(r => setTopics(r.data.topics));
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: '', brief: '', min_words: 150, reward_amount: 30 }); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setLoading(true);
    try {
      if (editing) {
        await api.patch(`/admin/blog-topics/${editing}`, form);
        toast.success('Topic updated');
      } else {
        await api.post('/admin/blog-topics', form);
        toast.success('Topic created');
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save topic');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (t) => { setEditing(t.id); setForm({ title: t.title, brief: t.brief || '', min_words: t.min_words, reward_amount: t.reward_amount }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const toggle = async (id, is_active) => { await api.patch(`/admin/blog-topics/${id}`, { is_active }); load(); };
  const remove = async (id) => { if (!window.confirm('Delete this topic?')) return; await api.delete(`/admin/blog-topics/${id}`); toast.success('Deleted'); load(); };

  return (
    <div>
      <div className="form-card" style={{ maxWidth: 600 }}>
        <h2 style={{ marginBottom: 20 }}>{editing ? 'Edit Topic' : 'Add Blog Topic'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Why I Joined Drivenwave" required />
          </div>
          <div className="form-group">
            <label>Brief</label>
            <textarea rows={3} value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })} placeholder="What the post should cover" />
          </div>
          <div className="form-group">
            <label>Minimum Words *</label>
            <input type="number" min="20" value={form.min_words} onChange={e => setForm({ ...form, min_words: Number(e.target.value) })} required />
          </div>
          <div className="form-group">
            <label>Reward (KES) *</label>
            <input type="number" min="1" value={form.reward_amount} onChange={e => setForm({ ...form, reward_amount: Number(e.target.value) })} required />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="btn-primary" disabled={loading} style={{ flex: 1 }}>
              {loading ? 'Saving...' : editing ? 'Update Topic' : 'Add Topic'}
            </button>
            {editing && <button type="button" className="btn-primary" style={{ flex: 1, background: '#334155' }} onClick={resetForm}>Cancel</button>}
          </div>
        </form>
      </div>

      <h2 style={{ marginBottom: 16, fontSize: '1.1rem' }}>All Topics ({topics.length})</h2>
      <div className="table-wrapper">
        <table className="data-table">
          <thead><tr><th>Title</th><th>Min Words</th><th>Reward</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {topics.map(t => (
              <tr key={t.id}>
                <td><strong>{t.title}</strong></td>
                <td>{t.min_words}</td>
                <td>KES {t.reward_amount}</td>
                <td><span className={`badge ${t.is_active ? 'active' : 'inactive'}`}>{t.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="action-btns">
                  <button className="btn-sm" onClick={() => startEdit(t)}>Edit</button>
                  <button className="btn-sm" style={{ color: t.is_active ? '#ef4444' : '#22c55e', borderColor: t.is_active ? '#ef4444' : '#22c55e' }} onClick={() => toggle(t.id, !t.is_active)}>
                    {t.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button className="btn-sm red" onClick={() => remove(t.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {topics.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 32 }}>No topics yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── BLOG SUBMISSIONS (review) ─────────────────────────────────
const BlogSubmissionsAdmin = () => {
  const [subs, setSubs] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get('/admin/blog-submissions').then(r => setSubs(r.data.submissions));
  useEffect(() => { load(); }, []);

  const review = async (id, status) => {
    try {
      await api.patch(`/admin/blog-submissions/${id}`, { status });
      toast.success(`Submission ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to review submission');
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: 16, fontSize: '1.1rem' }}>Blog Submissions ({subs.length})</h2>
      <div className="table-wrapper">
        <table className="data-table">
          <thead><tr><th>User</th><th>Topic</th><th>Reward</th><th>Status</th><th>Submitted</th><th>Actions</th></tr></thead>
          <tbody>
            {subs.map(s => (
              <React.Fragment key={s.id}>
                <tr>
                  <td>{s.users?.full_name}<br /><span style={{ color: '#64748b', fontSize: '0.78rem' }}>{s.users?.phone}</span></td>
                  <td>{s.blog_topics?.title}</td>
                  <td>KES {s.earning_amount}</td>
                  <td><span className={`submission-badge ${s.status}`}>{s.status}</span></td>
                  <td style={{ fontSize: '0.8rem' }}>{new Date(s.submitted_at).toLocaleDateString()}</td>
                  <td className="action-btns">
                    <button className="btn-sm" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                      {expanded === s.id ? 'Hide' : 'View'}
                    </button>
                    {s.status === 'pending' && (
                      <>
                        <button className="btn-sm" style={{ color: '#22c55e', borderColor: '#22c55e' }} onClick={() => review(s.id, 'approved')}>Approve</button>
                        <button className="btn-sm red" onClick={() => review(s.id, 'rejected')}>Reject</button>
                      </>
                    )}
                  </td>
                </tr>
                {expanded === s.id && (
                  <tr>
                    <td colSpan={6} style={{ background: 'var(--surface2)', whiteSpace: 'pre-wrap', fontSize: '0.85rem', padding: 16 }}>
                      {s.content}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {subs.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#64748b', padding: 32 }}>No submissions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
